import { useSnapshotDiff } from "../api/use-snapshot-diff.js";
import { formatBytes } from "./asset-helpers.js";

// 3.B.3.3: Per slider-pysähdys delta-summary "+N uutta, ~M muutettu, -K poistettu".
// Vertaa valittua snapshotia uusimpaan snapshotiin (käytännössä "nykyhetki"
// daemon-puolen kannalta). Eri-snapshot:in valinta laukaisee uuden diff:n,
// 5 min daemon-cache + web-cache nopeuttavat takaisin-edestakaisin-liu'utusta.

export type SnapshotDeltaSummaryProps = {
  machineId: string;
  selectedSnapshotId: string;
  /** Vertailupiste — käytännössä uusin snapshot. */
  baselineSnapshotId: string;
};

export function SnapshotDeltaSummary({
  machineId,
  selectedSnapshotId,
  baselineSnapshotId,
}: SnapshotDeltaSummaryProps) {
  const diff = useSnapshotDiff(
    machineId,
    selectedSnapshotId,
    baselineSnapshotId,
    null
  );

  if (selectedSnapshotId === baselineSnapshotId) {
    return (
      <p className="text-xs text-slate-500">
        Tämä on uusin snapshot — ei eroja nykyhetkeen.
      </p>
    );
  }

  if (diff.status === "loading") {
    return <p className="text-xs text-slate-500">Lasketaan eroja nykyhetkeen…</p>;
  }
  if (diff.status === "failed") {
    return (
      <p className="text-xs text-yellow-300" title={diff.error}>
        Diff-haku ei onnistunut: {diff.error}
      </p>
    );
  }
  if (diff.status !== "completed") return null;

  const s = diff.data.summary;
  const noChanges =
    s.addedCount === 0 && s.modifiedCount === 0 && s.removedCount === 0;

  if (noChanges) {
    return (
      <p className="text-xs text-slate-500">
        Ei eroja uusimpaan snapshot:iin verrattuna.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3 text-xs">
      <DeltaCard
        sign="+"
        color="emerald"
        count={s.addedCount}
        bytes={s.addedBytes}
        label="uutta"
      />
      <DeltaCard
        sign="~"
        color="yellow"
        count={s.modifiedCount}
        bytes={s.modifiedBytes}
        label="muutettu"
      />
      <DeltaCard
        sign="-"
        color="rose"
        count={s.removedCount}
        bytes={s.removedBytes}
        label="poistettu"
      />
    </div>
  );
}

function DeltaCard({
  sign,
  color,
  count,
  bytes,
  label,
}: {
  sign: string;
  color: "emerald" | "yellow" | "rose";
  count: number;
  bytes: number;
  label: string;
}) {
  const palette = {
    emerald: "border-emerald-800/60 bg-emerald-950/30 text-emerald-200",
    yellow: "border-yellow-800/60 bg-yellow-950/30 text-yellow-200",
    rose: "border-rose-800/60 bg-rose-950/30 text-rose-200",
  }[color];
  return (
    <div className={`rounded-md border ${palette} px-2 py-1.5`}>
      <p className="font-medium">
        {sign}
        {count.toLocaleString("fi-FI")} {label}
      </p>
      {bytes > 0 && <p className="opacity-70">{formatBytes(bytes)}</p>}
    </div>
  );
}
